import { ReactElement, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { CabModal } from '@CabComponents/CabModal';
import { CabButton } from '@CabComponents/CabButton';
import CabRTEditor from '@CabComponents/CabRTEditor';
import { CabIcon } from '@CabComponents/CabIcon';
import { CabSpinner } from '@CabComponents/CabSpinner';
import { IoPeopleOutline, IoLocationOutline, IoTimeOutline } from 'react-icons/io5';
import { DateTime } from 'luxon';
import colors from '../../../colors';
import { AnalyticsEvent } from '../../../store/cabinetApi/generated/analytics';
import { EventBrief } from '../../../store/cabinetApi/generated/crm';
import { crm } from '../../../store/cabinetApi/crm';
import { cabCaptureException } from '../../../utils/logging';
import { skipToken } from '@reduxjs/toolkit/query';

// eslint-disable-next-line max-len
const EMPTY_STATE = '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

interface EventDetailModalProps {
  open: boolean;
  onClose: () => void;
  event: AnalyticsEvent | null;
  briefData: EventBrief | null | undefined;
  briefLoading: boolean;
  briefNotFound?: boolean;
  isUpdatingBrief: boolean;
  onUpdateBrief: (updatedBrief: Partial<EventBrief>) => Promise<void>;
  userHasBriefFeature: boolean;
}

const EventDetailModal = ({
  open,
  onClose,
  event,
  briefData,
  briefLoading,
  briefNotFound,
  isUpdatingBrief,
  onUpdateBrief,
  userHasBriefFeature
}: EventDetailModalProps): ReactElement => {
  const [briefContent, setBriefContent] = useState(EMPTY_STATE);
  const [notesContent, setNotesContent] = useState(EMPTY_STATE);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch attendee details using CRM contacts API
  const { data: attendeeData, isLoading: isLoadingAttendees } = crm.useAppCrmContactsListQuery(
    // @ts-expect-error - idIn type is generating incorrectly
    open && event && event.attendees.length > 0
      ? { idIn: event.attendees, limit: 1000 }
      : skipToken
  );

  const attendees = attendeeData?.data || [];

  useEffect(() => {
    if (briefData === undefined) {
      return;
    }
    
    if (briefData?.content && typeof briefData.content === 'object') {
      try {
        const jsonString = JSON.stringify(briefData.content);
        JSON.parse(jsonString);
        setBriefContent(jsonString);
      } catch (error) {
        cabCaptureException(error);
        setBriefContent(EMPTY_STATE);
      }
    } else {
      setBriefContent(EMPTY_STATE);
    }

    if (briefData?.notes && typeof briefData.notes === 'object') {
      try {
        const jsonString = JSON.stringify(briefData.notes);
        JSON.parse(jsonString);
        setNotesContent(jsonString);
      } catch (error) {
        cabCaptureException(error);
        setNotesContent(EMPTY_STATE);
      }
    } else {
      setNotesContent(EMPTY_STATE);
    }
    
    setIsLoading(false);
  }, [briefData]);

  const handleSaveBrief = async () => {
    if (!briefData) return;

    try {
      await onUpdateBrief({
        content: JSON.parse(briefContent)
      });
    } catch (error) {
      cabCaptureException(error);
    }
  };

  const handleSaveNotes = async () => {
    if (!briefData) return;

    try {
      await onUpdateBrief({
        notes: JSON.parse(notesContent)
      });
    } catch (error) {
      cabCaptureException(error);
    }
  };

  const formatDateTime = (dateString: string) => {
    return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_FULL);
  };

  const formatTime = (dateString: string) => {
    return DateTime.fromISO(dateString).toFormat('h:mma').toLowerCase();
  };

  if (!event) {
    return <></>;
  }

  return (
    <CabModal
      open={open}
      onClose={onClose}
      title={event.title}
      closeIcon
      sx={{
        '& .MuiDialog-paper': {
          width: '90%',
          maxWidth: 1000,
          maxHeight: '95vh',
        }
      }}
      actionButtons={
        <>
          <CabButton
            buttonType="primary"
            onClick={onClose}
          >
            Close
          </CabButton>
        </>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "80vh",
          gap: 2,
          overflow: "hidden"
        }}>
        {/* Event Details Section - Fixed at top, max height 400px */}
        <Box sx={{ 
          flexShrink: 0, 
          maxHeight: 400, 
          overflow: 'auto',
          border: `1px solid ${colors.black200}`,
          borderRadius: 1,
          p: 2, pt: 1, pb: 1,
        }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between"
              }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}>
                <CabIcon Icon={IoTimeOutline} sx={{ color: colors.black800, fontSize: 16 }} />
                <Typography variant="body2" color={colors.black800}>
                  {formatDateTime(event.start_date)} - {formatTime(event.end_date)}
                </Typography>
              </Box>
              {event.external_event_locations && event.external_event_locations.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}>
                  <CabIcon Icon={IoLocationOutline} sx={{ color: colors.black800, fontSize: 16 }} />
                  <Typography variant="body2" color={colors.black800}>
                    {event.external_event_locations[0].name}
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* <Box display="flex" alignItems="center" gap={1}>
              <CabIcon Icon={IoCalendarClearOutline} sx={{ color: colors.black800, fontSize: 16 }} />
              <Typography variant="body2" color={colors.black800}>
                {formatDate(event.calendar)}
              </Typography>
            </Box> */}

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1
              }}>
              <CabIcon Icon={IoPeopleOutline} sx={{ color: colors.black800, fontSize: 16, marginTop: '2px' }} />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column"
                }}>
                <Typography variant="body2" color={colors.black800}>
                  {event.attendees.length} attendee{event.attendees.length !== 1 ? 's' : ''}
                </Typography>
                {event.attendees.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.1
                    }}>
                    {isLoadingAttendees ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1
                        }}>
                        <CabSpinner scale={1} />
                        <Typography variant="body2" color={colors.black600} sx={{ fontSize: '0.875rem' }}>
                          Loading attendee details...
                        </Typography>
                      </Box>
                    ) : (
                      attendees.map((attendee) => (
                        <Typography 
                          key={attendee.id} 
                          variant="body2" 
                          color={colors.black600}
                          sx={{ fontSize: '0.875rem' }}
                        >
                          {attendee.name || attendee.primary_email || 'unknown'}
                          {attendee.name && attendee.primary_email && (
                            <span style={{ color: colors.black600 }}> • {attendee.primary_email}</span>
                          )}
                        </Typography>
                      ))
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {!briefNotFound && (
          <>
            {/* Meeting Brief Section - Fills remaining space */}
            <Box sx={{ 
              flex: 1, 
              // height: 400,
              display: 'flex', 
              flexDirection: 'column',
              minHeight: 0,
              position: 'relative',
            }}>
              <Typography
                variant="h6"
                color={colors.navyDark}
                sx={{
                  fontWeight: 600,
                  fontSize: 18,
                  mb: 0.75,
                  flexShrink: 0
                }}>
                Meeting Brief
              </Typography>
              <Box 
                sx={{
                  flex: 1,
                  borderRadius: 1,
                  // backgroundColor: colors.white900,
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {briefLoading || isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%"
                    }}>
                    <CabSpinner scale={2} />
                  </Box>
                ) : (
                  <>
                    <CabRTEditor
                      placeholder="Start writing your meeting summary..."
                      onChange={setBriefContent}
                      editorState={briefContent}
                      autoFocus={false}
                      editorInputClassName="brief-editor-input"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        borderColor: colors.black200,
                        flexDirection: 'column',
                        '& > div:last-child': {
                          flex: 1,
                          overflow: 'auto',
                          minHeight: 0
                        },
                        '& .brief-editor-input': {
                          height: '100% !important',
                          overflow: 'visible !important',
                          minHeight: '0 !important',
                          maxHeight: 'none !important',
                          outline: 'none !important',
                          '&:focus': {
                            outline: 'none !important'
                          },
                          padding: 1,
                          paddingTop: 2,
                        }
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 20,
                        right: 28
                      }}>
                      <CabButton
                        size="small"
                        buttonType="tertiary"
                        onClick={handleSaveBrief}
                        disabled={isUpdatingBrief}
                        sx={{ backgroundColor: `${colors.white900}AA` }}
                      >
                        {isUpdatingBrief ? 'Saving...' : 'Save Summary'}
                      </CabButton>
                    </Box>
                  </>
                )}
              </Box>
            </Box>

            {/* Meeting Notes Section - Fixed height at bottom */}
            <Box sx={{ 
              height: 220, 
              display: 'flex', 
              flexDirection: 'column', 
              flexShrink: 0
            }}>
              <Typography
                variant="h6"
                color={colors.navyDark}
                sx={{
                  fontWeight: 600,
                  fontSize: 18,
                  mb: 0.75
                }}>
                Meeting Notes
              </Typography>
              <Box 
                sx={{
                  flex: 1,
                  borderRadius: 1,
                  backgroundColor: colors.white900,
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {briefLoading || isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%"
                    }}>
                    <CabSpinner scale={2} />
                  </Box>
                ) : (
                  <>
                    <CabRTEditor
                      placeholder="Add your meeting notes here..."
                      onChange={setNotesContent}
                      editorState={notesContent}
                      autoFocus={false}
                      editorInputClassName="notes-editor-input"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        borderColor: colors.black200,
                        flexDirection: 'column',
                        '& > div:last-child': {
                          flex: 1,
                          overflow: 'auto',
                          minHeight: 0
                        },
                        '& .notes-editor-input': {
                          height: '100% !important',
                          overflow: 'visible !important',
                          minHeight: '0 !important',
                          maxHeight: 'none !important',
                          outline: 'none !important',
                          '&:focus': {
                            outline: 'none !important'
                          },
                          padding: 1,
                          paddingTop: 2,
                        }
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 18,
                        right: 28
                      }}>
                      <CabButton
                        size="small"
                        buttonType="tertiary"
                        onClick={handleSaveNotes}
                        disabled={isUpdatingBrief}
                        sx={{ backgroundColor: `${colors.white900}AA` }}
                      >
                        {isUpdatingBrief ? 'Saving...' : 'Save Notes'}
                      </CabButton>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </CabModal>
  );
};

export default EventDetailModal; 